import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Button,
    Typography,
    Alert,
} from "@material-tailwind/react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import RHFInput from "components/form/rhf-input";
import { useRateLimit, sanitiseInput } from "utils/utils.jsx";
import { toast } from "react-toastify";
import { QRCodeSVG } from "qrcode.react";
import {
    classifyError,
    getApiErrorMessage,
} from "utils/api-error";
import { totpField } from "utils/validation";

// Lives inside the <Dialog> so every open remounts the form and clears the
// code (recipe P5): RHF's store would otherwise survive close/reopen
// with a stale draft.
const TotpSetupForm = ({
    secretCode,
    qrCodeUrl,
    isLoading,
    setupError,
    isBlocked,
    incrementAttempts,
    resetAttempts,
    onVerify,
    onSetupSuccess,
    onSetupSkip,
    onSetupCancel,
}) => {
    const validationSchema = Yup.object().shape({
        totpCode: totpField.required("Authentication code is required"),
    });

    const methods = useForm({
        mode: "onChange",
        resolver: yupResolver(validationSchema),
        defaultValues: { totpCode: "" },
    });
    // Destructure the formState flags up front (P1): formState is a
    // subscription proxy and conditional reads drop subscriptions.
    const { isValid, isDirty, isSubmitting } = methods.formState;

    const handleSubmit = async (values) => {
        if (isBlocked) {
            toast.error("Too many attempts. Please try again later.");
            return;
        }
        try {
            const sanitisedCode = sanitiseInput(values.totpCode?.replace(/\s+/g, ""));
            await onVerify(sanitisedCode, secretCode);
            resetAttempts();
            onSetupSuccess();
        } catch (error) {
            const status = classifyError(error);
            const isAuthFailure = status === 401 || status === 403;
            if (isAuthFailure) {
                incrementAttempts();
            }
            methods.setError("totpCode", {
                type: "manual",
                message: isAuthFailure
                    ? "Invalid authentication code. Please try again."
                    : getApiErrorMessage(status, "totp"),
            });
        }
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(handleSubmit)}>
                <DialogBody className="dialog__body">
                    <div className="font-body py-4">
                        {isLoading ? (
                            <Typography className="text-center py-8">
                                Loading TOTP setup...
                            </Typography>
                        ) : setupError ? (
                            <Alert color="red" className="mb-4">
                                {setupError}
                            </Alert>
                        ) : (
                            <>
                                <Typography className="text-primary mb-4">
                                    1. Open the authenticator app on
                                    your phone
                                </Typography>
                                <Typography className="text-primary mb-2">
                                    {qrCodeUrl
                                        ? "2. Choose the option to either:"
                                        : "2. Manually enter this key:"}
                                </Typography>
                                <div className="flex flex-col items-center mb-4">
                                    {qrCodeUrl && (
                                        <>
                                            <Typography className="text-sm text-primary mt-2">
                                                Scan the QR Code
                                            </Typography>
                                            <QRCodeSVG
                                                value={qrCodeUrl}
                                                size={200}
                                            />
                                        </>
                                    )}
                                    {qrCodeUrl && (
                                        <Typography className="text-primary my-2">
                                            or
                                        </Typography>
                                    )}
                                    <Typography className="text-sm text-primary">
                                        {qrCodeUrl && "Manually enter this key: "}
                                        <span className="font-mono">
                                            {secretCode}
                                        </span>
                                    </Typography>
                                </div>
                                <Typography className="text-primary mb-2">
                                    3. Enter the 6 digit number
                                    generated by your authenticator
                                    app in the box below.
                                </Typography>
                                {isBlocked && (
                                    <Alert color="red" className="mt-2">
                                        Too many failed attempts.
                                        Please try again later.
                                    </Alert>
                                )}
                                <RHFInput
                                    autoComplete="one-time-code"
                                    className="font-display text-lg bg-white pt-4 pb-4"
                                    name="totpCode"
                                    type="text"
                                    color="black"
                                    label="Authentication Code"
                                    required
                                />
                            </>
                        )}
                    </div>
                </DialogBody>
                <DialogFooter className="dialog__footer pt-0">
                    <div className="flex gap-2 w-full">
                        {onSetupCancel && (
                            <Button
                                type="button"
                                className="btn btn--secondary"
                                variant="outlined"
                                onClick={onSetupCancel}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                        )}
                        {onSetupSkip && (
                            <Button
                                type="button"
                                className="btn btn--secondary"
                                variant="outlined"
                                onClick={onSetupSkip}
                            >
                                Skip for Now
                            </Button>
                        )}
                        <Button
                            type="submit"
                            className="btn btn--primary flex-1"
                            variant="filled"
                            disabled={
                                !(isValid && isDirty) ||
                                isSubmitting ||
                                isBlocked ||
                                isLoading ||
                                !!setupError
                            }
                        >
                            {isSubmitting
                                ? "Verifying..."
                                : "Verify and Enable"}
                        </Button>
                    </div>
                </DialogFooter>
            </form>
        </FormProvider>
    );
};

const TotpSetupDialog = ({
    open,
    onClose,
    onSetupSuccess,
    onSetupSkip,
    onSetupCancel,
    secretCode,
    secret32,
    userEmail,
    onVerify,
    isLoading = false,
    setupError = "",
}) => {
    const { isBlocked, incrementAttempts, resetAttempts } = useRateLimit();

    const qrCodeUrl =
        userEmail && secret32
            ? `otpauth://totp/Gobby:${encodeURIComponent(userEmail)}?secret=${secret32}&issuer=Gobby`
            : "";

    return (
        <Dialog
            open={open}
            handler={onClose}
            dismissible={false}
            className="dialog"
        >
            <div className="bg-white rounded-xl">
                <DialogHeader className="dialog__header">
                    Set Up Two-Factor Authentication
                </DialogHeader>
                <TotpSetupForm
                    secretCode={secretCode}
                    qrCodeUrl={qrCodeUrl}
                    isLoading={isLoading}
                    setupError={setupError}
                    isBlocked={isBlocked}
                    incrementAttempts={incrementAttempts}
                    resetAttempts={resetAttempts}
                    onVerify={onVerify}
                    onSetupSuccess={onSetupSuccess}
                    onSetupSkip={onSetupSkip}
                    onSetupCancel={onSetupCancel}
                />
            </div>
        </Dialog>
    );
};

export default TotpSetupDialog;
